import React from "react";
import { keyframes } from "@emotion/react";
import { Box, Stack, Typography, Chip } from "@mui/material";
import Iconify from "./Iconify";
import PhoneFrame from "./PhoneFrame";
import useAutoPlayLoop from "../hooks/useAutoPlayLoop";
import { INK, ACCENT, ACCENT_DARK, BELT_COLORS, EASE_OUT } from "../theme/brand";

// Rein dekorative Bedien-Demo (keine echten Daten) – simuliert einen
// kurzen Ausschnitt, wie jemand durch die App tippt: Start → Mitglieder →
// Mitglied antippen → Details → zurück, als Endlos-Loop.
const MEMBERS = [
  { name: "Jonas Reiter", belt: BELT_COLORS[4] },
  { name: "Amelie Vogt", belt: BELT_COLORS[2] },
  { name: "Kenji Noda", belt: BELT_COLORS[5] },
  { name: "Pia Hartmann", belt: BELT_COLORS[1] },
];

const TAPPED = MEMBERS[1];

const SCENES = [
  { screen: "home", duration: 1900 },
  { screen: "home", duration: 480, tap: { x: "32%", y: "91%" } },
  { screen: "list", duration: 1900 },
  { screen: "list", duration: 480, tap: { x: "50%", y: "32%" } },
  { screen: "detail", duration: 2300 },
  { screen: "detail", duration: 480, tap: { x: "11%", y: "7%" } },
];

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(0.4); opacity: 0.55; }
  70% { transform: scale(1.7); opacity: 0.15; }
  100% { transform: scale(2); opacity: 0; }
`;

function StatusBar() {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "grey.900" }}>9:41</Typography>
      <Iconify icon="mdi:signal-cellular-3" width={13} sx={{ color: "grey.700" }} />
    </Stack>
  );
}

const NAV_ITEMS = [
  { key: "home", icon: "mdi:view-dashboard", label: "Start" },
  { key: "list", icon: "mdi:account-group", label: "Mitglieder" },
  { key: "pay", icon: "mdi:bank-transfer", label: "Kasse" },
  { key: "more", icon: "mdi:dots-horizontal", label: "Mehr" },
];

function BottomNav({ active }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-around"
      sx={{ py: 1, bgcolor: "#fff", borderTop: "1px solid", borderColor: "grey.100" }}
    >
      {NAV_ITEMS.map((item) => {
        const isActive = active === item.key;
        return (
          <Stack key={item.key} alignItems="center" spacing={0.3}>
            <Iconify icon={item.icon} width={18} sx={{ color: isActive ? ACCENT_DARK : "grey.400" }} />
            <Typography sx={{ fontSize: 8.5, fontWeight: 700, color: isActive ? ACCENT_DARK : "grey.400" }}>
              {item.label}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}

function HomeScreen() {
  const rows = [
    { icon: "mdi:account-group", label: "Mitglieder", value: "128" },
    { icon: "mdi:bank-transfer", label: "Heute fällig", value: "3" },
    { icon: "healthicons:i-exam-multiple-choice", label: "Nächste Prüfung", value: "Sa · 14:00" },
  ];
  return (
    <Box>
      <StatusBar />
      <Box sx={{ px: 2, pt: 0.5, pb: 1.75 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "grey.500" }}>Guten Morgen</Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: "grey.900" }}>Dojo Falken e.V.</Typography>
      </Box>
      <Stack spacing={1.1} sx={{ px: 2 }}>
        {rows.map((row) => (
          <Stack
            key={row.label}
            direction="row"
            alignItems="center"
            spacing={1.1}
            sx={{ bgcolor: "#fff", borderRadius: 2, p: 1.1, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                bgcolor: INK,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Iconify icon={row.icon} width={15} sx={{ color: ACCENT }} />
            </Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "grey.700", flexGrow: 1 }}>
              {row.label}
            </Typography>
            <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "grey.900" }}>{row.value}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function ListScreen() {
  return (
    <Box>
      <StatusBar />
      <Box sx={{ px: 2, pt: 0.25, pb: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: "grey.900" }}>Mitglieder</Typography>
        <Typography sx={{ fontSize: 10.5, color: "grey.500" }}>128 Mitglieder</Typography>
      </Box>
      <Stack spacing={0.25} sx={{ px: 1.5 }}>
        {MEMBERS.map((m) => (
          <Stack key={m.name} direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.85, borderRadius: 1.5 }}>
            <Box sx={{ width: 6, height: 22, borderRadius: 3, bgcolor: m.belt, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "grey.800" }}>{m.name}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function DetailScreen() {
  return (
    <Box>
      <StatusBar />
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 2, pt: 0.25, pb: 1.5 }}>
        <Iconify icon="mdi:arrow-left" width={15} sx={{ color: "grey.500" }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "grey.500" }}>Mitglieder</Typography>
      </Stack>
      <Stack alignItems="center" spacing={0.75} sx={{ px: 2, pb: 1.75 }}>
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: "50%",
            bgcolor: INK,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ color: ACCENT, fontWeight: 800, fontSize: 16 }}>AV</Typography>
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: "grey.900" }}>{TAPPED.name}</Typography>
        <Chip
          size="small"
          label="Grüner Gürtel"
          sx={{ bgcolor: "rgba(43,138,62,0.1)", color: "#2b8a3e", fontWeight: 700, fontSize: 10.5 }}
        />
      </Stack>
      <Box sx={{ px: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ bgcolor: "#fff", borderRadius: 2, p: 1.1, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
        >
          <Typography sx={{ fontSize: 11, color: "grey.600", fontWeight: 600 }}>Beitrag · November</Typography>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Iconify icon="mdi:check-circle" width={12} sx={{ color: "#2b8a3e" }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#2b8a3e" }}>Abgebucht</Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

const SCREENS = { home: HomeScreen, list: ListScreen, detail: DetailScreen };
const NAV_ACTIVE = { home: "home", list: "list", detail: "list" };

export default function PhoneUsageDemo() {
  const { ref, step } = useAutoPlayLoop(SCENES);
  const Screen = SCREENS[step.screen];

  return (
    <Box ref={ref} aria-hidden="true" sx={{ display: "flex", justifyContent: "center" }}>
      <PhoneFrame>
        <Box sx={{ position: "relative", height: "100%" }}>
          <Box
            key={step.screen}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              animation: `${fadeIn} 360ms ${EASE_OUT}`,
            }}
          >
            <Box sx={{ flex: 1, overflow: "hidden" }}>
              <Screen />
            </Box>
            <BottomNav active={NAV_ACTIVE[step.screen]} />
          </Box>

          {step.tap && (
            <Box
              key={`tap-${step.screen}-${step.tap.x}-${step.tap.y}`}
              sx={{
                position: "absolute",
                left: step.tap.x,
                top: step.tap.y,
                width: 34,
                height: 34,
                ml: "-17px",
                mt: "-17px",
                borderRadius: "50%",
                bgcolor: ACCENT_DARK,
                animation: `${pulse} 480ms ${EASE_OUT}`,
                pointerEvents: "none",
              }}
            />
          )}
        </Box>
      </PhoneFrame>
    </Box>
  );
}
